import { nextTick, onBeforeUnmount } from 'vue'
import { useScrollToHash } from '@/composables/useScrollToHash'
import { isScrollableHashLink } from '@/utilities/url'

type RegisterOptions = {
  withInitialScroll?: boolean
}

export const useHashLinkHandler = () => {
  const { scrollToHash } = useScrollToHash()
  const registeredAnchors = new Set<HTMLAnchorElement>()
  // registerHashLinkClickHandler の nextTick 前に unmount された場合、遅れてリスナが登録され続けるのを防ぐ
  let isUnmounted = false

  const getScrollableHashLinks = (): HTMLAnchorElement[] => {
    return Array.from(document.querySelectorAll('a[href*="#"]'))
      .filter(isScrollableHashLink)
  }

  /**
   * スムーススクロール対象のハッシュリンクをクリックした際に、ブラウザでのページ遷移ではなく、ライブラリでのスムーススクロールで遷移させる
   * @param e イベントオブジェクト
   */
  function hashClickHandler(e: Event) {
    // 複数のテンプレートやコンポーネントが同一リンクに個別にハンドラを登録し得るため、
    // 既に一方が処理（preventDefault）済みなら何もしない（スムーススクロールの二重発火・ちらつき防止）
    if (e.defaultPrevented) return

    const target = e.currentTarget as HTMLAnchorElement
    if (isScrollableHashLink(target)) {
      const id = target.hash.slice(1) // ハッシュの先頭の「#」を削除

      // スクロール先が存在しない場合、scrollToHash は何もせず return する。
      // その後に preventDefault すると、ブラウザ標準のアンカー挙動だけを止めてしまうため、ここで標準挙動に任せる。
      if (!document.getElementById(id)) return

      scrollToHash(id)
      e.preventDefault() // ブラウザ標準のジャンプを止める

      /**
       * 同一ページ内の他ハッシュへのリンクをクリックした場合
       * （例：foo#bar というアドレスの画面をレンダリング時、foo#baz へのリンクをクリックしてハッシュ遷移した場合）
       * 直前で preventDefault しているため、アドレスが書き換わらない現象が発生してしまうので（ブラウザのアンカーイベントを preventDefault で殺しているため）、
       * 手動で history を書き換えてアドレスを更新する。（pushState だと遷移した分全て履歴扱いになり、ブラウザバックの際に同一画面で多重に履歴が蓄積されるため、replaceState で 1 画面 1 履歴とする）
       */
      history.replaceState(null, '', target.hash)
    }
  }

  /**
   * ページ内ハッシュリンクにスムーススクロール用のクリックハンドラを登録する。
   * withInitialScroll は、ページ全体をマウントするテンプレートからのみ true を渡す。
   */
  const registerHashLinkClickHandler = (options: RegisterOptions = {}) => {
    nextTick(() => {
      if (isUnmounted) return

      // ページ内のアンカー要素をすべて拾い、スムーススクロール対象のハッシュリンクであれば、ブラウザの遷移イベントをキャンセルし、ライブラリでのスムーススクロールで遷移させる
      getScrollableHashLinks().forEach(anchor => {
        anchor.addEventListener('click', hashClickHandler)
        registeredAnchors.add(anchor)
      })

      if (options.withInitialScroll) {
        // ページ初回レンダリング時のハッシュ遷移
        scrollToHash()
      }
    })
  }

  function unregisterHashLinkClickHandler() {
    registeredAnchors.forEach(anchor => {
      anchor.removeEventListener('click', hashClickHandler)
    })
    registeredAnchors.clear()
  }

  onBeforeUnmount(() => {
    isUnmounted = true
    unregisterHashLinkClickHandler()
  })

  return {
    registerHashLinkClickHandler,
  }
}
